import React, { useEffect, useMemo, useState, type RefObject } from "react";

import { Building2, CheckCircle2, History, Loader2, Users } from "lucide-react";
import { createPortal } from "react-dom";

import ActionCard from "./ActionCard";
import MyAccountsActionHistory from "./MyAccountsActionHistory";
import PreCallBriefModal, {
  type PreCallBriefTarget,
} from "./PreCallBriefModal";
import { bucketFor, type SignalBucket } from "./signalCTAs";
import { useSignalCardsWithAccountContext } from "./useSignalCardsWithAccountContext";
import {
  SignalsOffNotice,
  useSignalPresentation,
} from "./useSignalPresentation";
import WorkingSetBanner from "./WorkingSetBanner";
import SearchInput from "../../Components/Search/SearchInput";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  dismissSignal,
  fetchSignalFeed,
  snoozeSignal,
  type SignalTier,
} from "../../Store/Reducers/SignalFeedSlice";

const SUB_TAB_ICON_CLASS = "h-[18px] w-[18px]";
const TIER_ORDER: SignalTier[] = ["critical", "warning", "opp", "maint"];
const TIER_SECTION_LABEL: Record<SignalTier, string> = {
  critical: "Critical",
  warning: "Warning",
  opp: "Opportunity",
  maint: "Maintenance",
};

// Mock's TIER_DESC (my-accounts-portfolio-v4.html:4125-4126) and .ush-inner /
// .pill-* colors (lines 482-522), used for the tier-group banner.
const TIER_DESC: Record<SignalTier, string> = {
  critical: "Immediate attention required",
  warning: "Review and act this week",
  opp: "Expansion or advancement opportunity",
  maint: "On track — periodic check-in",
};
const TIER_COLOR: Record<SignalTier, string> = {
  critical: "#dc2626",
  warning: "#d97706",
  opp: "#0d9488",
  maint: "#9ca3af",
};

interface ActionsTabProps {
  withOrg: (path: string) => string;
  onOpenAssess: (account: { id: string; name: string }) => void;
  workingSetAccountIds?: string[] | null;
  onClearWorkingSet?: () => void;
  headerFiltersRef?: RefObject<HTMLDivElement | null>;
  /** Set by a "View in Actions →" deep link (Portfolio Map's compact rail,
   * "See all portfolio actions →") to land on the right sub-tab, scroll to,
   * and briefly flash the account's card. Consumed once, then cleared. */
  highlightAccountId?: string | null;
  /** When set alongside highlightAccountId, flashes the specific firing
   * signal that was clicked instead of just the first card for the account. */
  highlightSignalKey?: string | null;
  onHighlightConsumed?: () => void;
}

const ActionsTab: React.FC<ActionsTabProps> = ({
  withOrg,
  onOpenAssess,
  workingSetAccountIds,
  onClearWorkingSet,
  headerFiltersRef,
  highlightAccountId,
  highlightSignalKey,
  onHighlightConsumed,
}) => {
  const dispatch = useAppDispatch();
  const { cards, loading, error, actionInFlightId, presentation } =
    useAppSelector(state => state.signalFeed);
  const { signalsVisible, status: presentationStatus } =
    useSignalPresentation();
  const [briefTarget, setBriefTarget] = useState<PreCallBriefTarget | null>(
    null
  );
  const [actionsSubTab, setActionsSubTab] = useState<SignalBucket | "history">(
    "account"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [flashFiringId, setFlashFiringId] = useState<string | null>(null);

  // fetchSignalFeed is dispatched once at the page level (index.tsx), not
  // here — mock: FEED_CARDS is a persistent in-memory list that survives tab
  // switches, only regenerated on an explicit Refresh or full page load
  // (generateFeed()'s callers). Re-dispatching on every mount would re-fetch
  // needlessly on tab switches. Mark-as-handled / Snooze are persisted via
  // SignalInteraction and honored by get_signal_feed, so a refresh keeps
  // those cards hidden for this user.
  const cardsWithContext = useSignalCardsWithAccountContext(cards);
  const workingSetIdSet = workingSetAccountIds
    ? new Set(workingSetAccountIds)
    : null;
  const workingSetScopedCards = workingSetIdSet
    ? cardsWithContext.filter(({ card }) =>
        workingSetIdSet.has(card.account_id)
      )
    : cardsWithContext;

  const searchedCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return workingSetScopedCards;
    return workingSetScopedCards.filter(({ card }) =>
      card.account_name.toLowerCase().includes(q)
    );
  }, [workingSetScopedCards, searchQuery]);

  const accountCards = useMemo(
    () =>
      searchedCards.filter(
        ({ card }) => bucketFor(card.signal_key) === "account"
      ),
    [searchedCards]
  );
  const relationshipCards = useMemo(
    () =>
      searchedCards.filter(
        ({ card }) => bucketFor(card.signal_key) === "relationship"
      ),
    [searchedCards]
  );
  const activeCards =
    actionsSubTab === "account" ? accountCards : relationshipCards;

  const subTabs = useMemo(
    () => [
      {
        id: "account",
        label: "Account Actions",
        count: accountCards.length,
        icon: <Building2 className={SUB_TAB_ICON_CLASS} aria-hidden />,
      },
      {
        id: "relationship",
        label: "Relationship Actions",
        count: relationshipCards.length,
        icon: <Users className={SUB_TAB_ICON_CLASS} aria-hidden />,
      },
      // §8.2 persona-level entry point (signal-service-design-note.md) —
      // no count badge: it lists accounts, not open signals, so a live
      // signal count would misrepresent what the tab shows.
      {
        id: "history",
        label: "History",
        icon: <History className={SUB_TAB_ICON_CLASS} aria-hidden />,
      },
    ],
    [accountCards.length, relationshipCards.length]
  );

  // Deep-link arrival: jump to the right sub-tab and briefly flash the card
  // for the account (and, when known, the specific signal) that was clicked
  // from elsewhere (e.g. Portfolio Map). Waits for the feed's first answer:
  // on a cold load the link arrives before the cards, and consuming it then
  // lost the flash (S-04). The account's one card is matched whether the
  // signal is its primary or behind "+N more".
  const feedSettled = (presentation !== null && !loading) || error !== null;
  useEffect(() => {
    if (!highlightAccountId || !feedSettled) return;
    const forAccount = cardsWithContext.filter(
      ({ card }) => card.account_id === highlightAccountId
    );
    const match =
      (highlightSignalKey &&
        forAccount.find(
          ({ card }) =>
            card.signal_key === highlightSignalKey ||
            (card.secondary ?? []).some(
              s => s.signal_key === highlightSignalKey
            )
        )) ||
      forAccount[0];
    if (match) {
      setActionsSubTab(bucketFor(match.card.signal_key));
      setFlashFiringId(match.card.signal_firing_id);
    }
    onHighlightConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightAccountId, feedSettled]);

  // Scroll to the flashing card once its sub-tab has rendered, and clear
  // the flash after a moment — independent of the deep link being consumed.
  useEffect(() => {
    if (!flashFiringId) return undefined;
    document
      .getElementById(`action-card-${flashFiringId}`)
      ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => setFlashFiringId(null), 1500);
    return () => window.clearTimeout(timer);
  }, [flashFiringId, actionsSubTab]);

  const grouped = TIER_ORDER.map(tier => ({
    tier,
    items: activeCards.filter(({ card }) => card.tier === tier),
  })).filter(group => group.items.length > 0);

  const headerFilters = headerFiltersRef?.current
    ? createPortal(
        <div className="w-[200px] shrink-0">
          <SearchInput
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            rounded="normal"
            size="sm"
          />
        </div>,
        headerFiltersRef.current
      )
    : null;

  const subTabsBar = (
    <div className="border-b border-gray-200">
      <TabsComponent
        tabs={subTabs}
        activeTab={actionsSubTab}
        onTabChange={id => setActionsSubTab(id as SignalBucket | "history")}
        variant="tab-underline"
        className="-mb-px mb-0"
      />
    </div>
  );

  // Signal Administration has turned signal output off for this org:
  // no open actions, only the read-only history (signals-design-note.md
  // §11.3). Checked before the history sub-tab so there's no route back
  // to the Account/Relationship sub-tabs. Only once the policy has loaded:
  // a policy that is still loading or failed to load is not "off" (S-02).
  if (presentationStatus === "ready" && !signalsVisible) {
    return (
      <div className="flex flex-col gap-5">
        <SignalsOffNotice showHistoryHint />
        <MyAccountsActionHistory />
      </div>
    );
  }

  // Rendered ahead of every signalFeed loading/error/empty guard below —
  // those all describe the live feed (`cards`), which has nothing to do
  // with History, and would otherwise hide the History sub-tab itself
  // whenever the feed happens to be empty. Those guards keep the sub-tab
  // bar too, so History stays reachable from them (SURF-06).
  if (actionsSubTab === "history") {
    return (
      <div className="flex flex-col gap-5">
        {subTabsBar}
        <MyAccountsActionHistory />
      </div>
    );
  }

  if (presentationStatus === "loading" || (loading && cards.length === 0)) {
    return (
      <div className="flex flex-col gap-5">
        {headerFilters}
        {subTabsBar}
        <div className="flex min-h-[240px] items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span className="ml-2 text-sm">Loading signals…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        {headerFilters}
        {subTabsBar}
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <span>Couldn&apos;t load your signal feed: {error}</span>
          <button
            type="button"
            onClick={() => void dispatch(fetchSignalFeed())}
            className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        {headerFilters}
        {subTabsBar}
        <div className="flex min-h-[240px] flex-col items-center justify-center text-center text-gray-400">
          <CheckCircle2 className="h-8 w-8" aria-hidden />
          <p className="mt-2 text-sm font-medium text-gray-500">
            Nothing needs your attention right now.
          </p>
          <p className="text-xs text-gray-400">
            New signals will show up here as they&apos;re detected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {headerFilters}
      <PreCallBriefModal
        target={briefTarget}
        onClose={() => setBriefTarget(null)}
        withOrg={withOrg}
      />

      {subTabsBar}

      {workingSetIdSet && onClearWorkingSet && (
        <WorkingSetBanner
          count={workingSetIdSet.size}
          onClear={onClearWorkingSet}
        />
      )}
      {(workingSetIdSet || searchQuery.trim()) && grouped.length === 0 && (
        <p className="text-xs text-gray-400">
          No {actionsSubTab === "account" ? "account" : "relationship"} actions
          match the current filter.
        </p>
      )}
      {grouped.map(group => {
        const tierColor = TIER_COLOR[group.tier];
        return (
          <div key={group.tier} className="mb-1">
            <div
              className="mb-3.5 flex flex-wrap items-center gap-2 rounded-md py-[7px] pl-3 pr-3.5"
              style={{
                borderLeft: `3px solid ${tierColor}`,
                background: `${tierColor}0D`,
              }}
            >
              <span
                className="rounded px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.07em]"
                style={{ background: `${tierColor}1F`, color: tierColor }}
              >
                {TIER_SECTION_LABEL[group.tier]}
              </span>
              <span className="text-[11px] text-gray-400">
                · {group.items.length} account
                {group.items.length === 1 ? "" : "s"}
              </span>
              <span className="text-[11px] italic text-gray-400">
                — {TIER_DESC[group.tier]}
              </span>
            </div>
            <div className="grid auto-cols-max grid-flow-col grid-rows-[auto_auto_1fr_auto] gap-x-4 overflow-x-auto pb-3.5 [scroll-snap-type:x_mandatory] [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              {group.items.map(({ card, context }) => (
                <div
                  key={card.signal_firing_id}
                  id={`action-card-${card.signal_firing_id}`}
                  className="row-span-4 grid shrink-0 grid-rows-subgrid [scroll-snap-align:start]"
                >
                  <ActionCard
                    alignInRow
                    card={card}
                    context={context}
                    busy={actionInFlightId === card.signal_firing_id}
                    highlighted={flashFiringId === card.signal_firing_id}
                    onSnooze={(firingId, days) =>
                      void dispatch(snoozeSignal({ firingId, days }))
                    }
                    onDismiss={firingId =>
                      void dispatch(dismissSignal(firingId))
                    }
                    withOrg={withOrg}
                    onOpenAssess={onOpenAssess}
                    onOpenBrief={(c, ctx) =>
                      setBriefTarget({ card: c, context: ctx })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActionsTab;
